import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Area,
  AreaChart,
} from 'recharts';
import { 
  MapPin, 
  Droplet, 
  Trees, 
  TrendingUp, 
  Users, 
  Zap,
  LogOut,
  FileText,
  BarChart3,
  Settings,
  Map as MapIcon,
  Filter,
  ChevronDown,
  ChevronUp,
  X
} from 'lucide-react';
import { mapTilerKey } from '../Constants';
import styles from './AskiDashboard.module.css';

// MapLibre GL'i CDN'den yükle
const maplibregl = window.maplibregl;

// Mock veriler
const mockData = {
  // Ana kartlar için veriler
  summaryCards: {
    totalParkArea: { value: 5000000, unit: 'm²', period: '2025/12 (Ay)', icon: MapPin },
    totalWaterConsumption: { value: 12500000, unit: 'm³', period: '2025/12 (Ay)', icon: Droplet },
    totalGreenArea: { value: 4500000, unit: 'm²', period: '2025/12 (Ay)', icon: Trees },
    greenAreaWaterConsumption: { value: 2.78, unit: 'm³/m²', period: '2025/12 (Ay)', icon: TrendingUp },
    population: { value: 5663322, unit: '', period: '2024', icon: Users }, // Ankara Nüfusu
    efficiency: { value: 85.5, unit: '%', period: '2025/12 (Ay)', icon: Zap },
  },
  
  // Belediyeler
  belediyeler: [
    { id: 1, name: 'Çankaya', nufus: 920000, parks: 500, parkArea: 1000000, greenArea: 900000, waterConsumption: 2500000 },
    { id: 2, name: 'Etimesgut', nufus: 620000, parks: 450, parkArea: 900000, greenArea: 800000, waterConsumption: 2200000 },
    { id: 3, name: 'Yenimahalle', nufus: 680000, parks: 400, parkArea: 800000, greenArea: 750000, waterConsumption: 2000000 },
    { id: 4, name: 'Büyükşehir', nufus: 5600000, parks: 1200, parkArea: 2300000, greenArea: 2050000, waterConsumption: 5800000 },
  ],

  // Parklar (her belediye için) - PARK 250 için yeterli veri
  parks: {
    1: Array.from({ length: 125 }, (_, i) => {
      const area = 30000 + Math.random() * 50000;
      const greenArea = area * (0.85 + Math.random() * 0.1);
      const idealConsumption = greenArea * 2.5;
      const waterConsumption = idealConsumption * (0.7 + Math.random() * 0.4);
      const efficiency = Math.round((idealConsumption / waterConsumption) * 100);
      return {
        id: i + 1,
        name: `Çankaya Park-${i + 1}`,
        area: Math.round(area),
        greenArea: Math.round(greenArea),
        waterConsumption: Math.round(waterConsumption),
        efficiency: Math.min(120, Math.max(60, efficiency)),
      };
    }),
    2: Array.from({ length: 113 }, (_, i) => {
      const area = 28000 + Math.random() * 48000;
      const greenArea = area * (0.85 + Math.random() * 0.1);
      const idealConsumption = greenArea * 2.5;
      const waterConsumption = idealConsumption * (0.7 + Math.random() * 0.4);
      const efficiency = Math.round((idealConsumption / waterConsumption) * 100);
      return {
        id: i + 1,
        name: `Etimesgut Park-${i + 1}`,
        area: Math.round(area),
        greenArea: Math.round(greenArea),
        waterConsumption: Math.round(waterConsumption),
        efficiency: Math.min(120, Math.max(60, efficiency)),
      };
    }),
    3: Array.from({ length: 100 }, (_, i) => {
      const area = 26000 + Math.random() * 46000;
      const greenArea = area * (0.85 + Math.random() * 0.1);
      const idealConsumption = greenArea * 2.5;
      const waterConsumption = idealConsumption * (0.7 + Math.random() * 0.4);
      const efficiency = Math.round((idealConsumption / waterConsumption) * 100);
      return {
        id: i + 1,
        name: `Yenimahalle Park-${i + 1}`,
        area: Math.round(area),
        greenArea: Math.round(greenArea),
        waterConsumption: Math.round(waterConsumption),
        efficiency: Math.min(120, Math.max(60, efficiency)),
      };
    }),
    4: Array.from({ length: 300 }, (_, i) => {
      const area = 50000 + Math.random() * 100000;
      const greenArea = area * (0.85 + Math.random() * 0.1);
      const idealConsumption = greenArea * 2.5;
      const waterConsumption = idealConsumption * (0.7 + Math.random() * 0.4);
      const efficiency = Math.round((idealConsumption / waterConsumption) * 100);
      return {
        id: i + 1,
        name: `Büyükşehir Park-${i + 1}`,
        area: Math.round(area),
        greenArea: Math.round(greenArea),
        waterConsumption: Math.round(waterConsumption),
        efficiency: Math.min(120, Math.max(60, efficiency)),
      };
    }),
  },

  // 10 yıllık trend verileri - Çeyreklik bazlı ve anomalilerle
  yearlyTrend: (() => {
    const data = [];
    const baseConsumption = 8500000;
    const baseParks = 1800;
    const baseArea = 3800000;
    const basePopulation = 78741053;
    
    for (let year = 2015; year <= 2025; year++) {
      for (let quarter = 1; quarter <= 4; quarter++) {
        const yearIndex = year - 2015;
        const quarterIndex = (yearIndex * 4) + (quarter - 1);
        
        // Trend: Genel artış ama bazı anomaliler
        let consumptionMultiplier = 1 + (yearIndex * 0.05) + (quarter * 0.01);
        let parksMultiplier = 1 + (yearIndex * 0.03) + (quarter * 0.005);
        let areaMultiplier = 1 + (yearIndex * 0.04) + (quarter * 0.008);
        let populationMultiplier = 1 + (yearIndex * 0.008) + (quarter * 0.002);
        
        // Anomaliler ekle (rastgele ama mantıklı yerlerde) - Daha fazla anomali
        if (year === 2015 && quarter === 4) {
          consumptionMultiplier *= 0.88; // Kış etkisi
        } else if (year === 2016 && quarter === 2) {
          consumptionMultiplier *= 1.18; // İlkbahar artışı
        } else if (year === 2016 && quarter === 3) {
          consumptionMultiplier *= 0.82; // Kuraklık
        } else if (year === 2017 && quarter === 1) {
          consumptionMultiplier *= 1.12; // Kış sonrası artış
        } else if (year === 2017 && quarter === 4) {
          consumptionMultiplier *= 0.92; // Sonbahar azalışı
        } else if (year === 2018 && quarter === 2) {
          consumptionMultiplier *= 1.28; // Sıcak yaz
        } else if (year === 2018 && quarter === 4) {
          consumptionMultiplier *= 0.95; // Normal kış
        } else if (year === 2019 && quarter === 1) {
          consumptionMultiplier *= 1.08; // İlkbahar başlangıcı
        } else if (year === 2019 && quarter === 3) {
          consumptionMultiplier *= 1.15; // Yaz artışı
        } else if (year === 2020 && quarter === 1) {
          consumptionMultiplier *= 0.88; // Pandemi başlangıcı
        } else if (year === 2020 && quarter === 2) {
          consumptionMultiplier *= 0.72; // Pandemi etkisi (büyük düşüş)
        } else if (year === 2020 && quarter === 3) {
          consumptionMultiplier *= 0.85; // Pandemi devam
        } else if (year === 2021 && quarter === 2) {
          consumptionMultiplier *= 1.22; // Normale dönüş artışı
        } else if (year === 2021 && quarter === 4) {
          consumptionMultiplier *= 0.93; // Kış azalışı
        } else if (year === 2022 && quarter === 1) {
          consumptionMultiplier *= 1.05; // İlkbahar
        } else if (year === 2022 && quarter === 3) {
          consumptionMultiplier *= 1.18; // Yaz artışı
        } else if (year === 2022 && quarter === 4) {
          consumptionMultiplier *= 1.12; // Kuraklık sonrası artış
        } else if (year === 2023 && quarter === 2) {
          consumptionMultiplier *= 0.90; // İlkbahar azalışı (yağışlı)
        } else if (year === 2023 && quarter === 4) {
          consumptionMultiplier *= 1.08; // Sonbahar artışı
        } else if (year === 2024 && quarter === 1) {
          consumptionMultiplier *= 0.87; // Kış etkisi
        } else if (year === 2024 && quarter === 3) {
          consumptionMultiplier *= 1.20; // Yaz artışı
        } else if (year === 2025 && quarter === 1) {
          consumptionMultiplier *= 0.94; // Kış azalışı
        } else if (year === 2025 && quarter === 2) {
          consumptionMultiplier *= 1.14; // İlkbahar artışı
        }
        
        // Rastgele dalgalanmalar (daha fazla varyasyon)
        const seasonalVariation = Math.sin((quarter - 1) * Math.PI / 2) * 0.08; // Mevsimsel değişim
        const randomVariation = 0.92 + (Math.random() * 0.16); // %8-16 arası rastgele değişim
        consumptionMultiplier *= (1 + seasonalVariation) * randomVariation;
        
        data.push({
          period: `${year}/Q${quarter}`,
          year: year.toString(),
          quarter: quarter,
          consumption: Math.round(baseConsumption * consumptionMultiplier),
          parks: Math.round(baseParks * parksMultiplier),
          area: Math.round(baseArea * areaMultiplier),
          population: Math.round(basePopulation * populationMultiplier),
        });
      }
    }
    return data;
  })(),

  // İşletme bilgileri
  isletme: {
    1: { kartli: 350, gprs: 120, tanker: 30, sabah: 200, oglen: 150, aksam: 100, gece: 50 },
    2: { kartli: 280, gprs: 140, tanker: 30, sabah: 180, oglen: 140, aksam: 90, gece: 40 },
    3: { kartli: 250, gprs: 120, tanker: 30, sabah: 170, oglen: 130, aksam: 80, gece: 40 },
    4: { kartli: 800, gprs: 300, tanker: 100, sabah: 600, oglen: 400, aksam: 200, gece: 100 },
  },
};

const LegacyAskiDashboard = () => {
  const { user, logout } = useAuth();
  const [activeCard, setActiveCard] = useState('totalParkArea');
  const [hoveredCard, setHoveredCard] = useState(null); // Hover için
  const [activePage, setActivePage] = useState('main'); // main, il-ilce, belediye, reports
  const [selectedBelediyeler, setSelectedBelediyeler] = useState([]);
  const [selectedBelediye, setSelectedBelediye] = useState(null);
  const [selectedBelediyelerForComparison, setSelectedBelediyelerForComparison] = useState([]);
  const [selectedIlcelerForChart, setSelectedIlcelerForChart] = useState([]); // Grafik için seçilen ilçeler
  
  // Her grafik için ayrı filtreleme state'leri
  const [chartFilters, setChartFilters] = useState({
    mainChart: { belediyeler: [], parklar: [] }, // Ana sayfa grafiği
    belediyeChart: { parklar: [] }, // Belediye özeti grafiği
    belediyeComparisonChart: { belediyeler: [] }, // Belediye karşılaştırma grafiği
    reportsChart: { belediyeler: [], parklar: [] }, // Raporlar grafiği
  });
  
  // Filtreleme paneli açık/kapalı durumları
  const [filterPanelsOpen, setFilterPanelsOpen] = useState({
    mainChart: false,
    belediyeChart: false,
    belediyeTable: false,
    ilIlceTable: false,
    reportsChart: false,
  });
  
  // Tablo filtreleri
  const [tableFilters, setTableFilters] = useState({
    ilIlceTable: { belediyeler: [] },
    belediyeTable: { parklar: [] },
    reportsTable: { belediyeler: [], parklar: [] },
  });
  
  const [showSettings, setShowSettings] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [comparisonCount, setComparisonCount] = useState(3); // Karşılaştırma için belediye sayısı
  const [showMap, setShowMap] = useState(false);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const [selectedParkForMap, setSelectedParkForMap] = useState(null);
  
  // Filtre state'leri
  const [filters, setFilters] = useState({
    parkSize: '',
    population: '',
    irrigationType: [], // Array olarak değiştirildi - çoklu seçim için
    meterType: '',
    region: '',
    efficiencyRange: '',
  });
  
  // Teorik vs Gerçek veriler için
  const getTheoreticalConsumption = (belediye) => {
    // Teorik tüketim: ideal verimlilik (%90) ile hesaplanan
    return belediye.waterConsumption * 0.9;
  };
  
  const getRealConsumption = (belediye) => {
    // Gerçek tüketim: mevcut tüketim
    return belediye.waterConsumption;
  };
  
  // Park verimlilikleri hesapla
  const getParkEfficiency = (park) => {
    // Verimlilik: ideal tüketim / gerçek tüketim * 100
    // İdeal tüketim: m² başına 2.5 m³/yıl (veya daha iyi bir formül)
    const ideal = park.greenArea * 2.5; // m² başına ideal 2.5 m³
    const real = park.waterConsumption;
    if (real === 0) return 0;
    // Verimlilik: ideal ne kadar az tüketiyorsa o kadar yüksek
    // Eğer gerçek < ideal ise %100+ olabilir (çok verimli)
    // Eğer gerçek > ideal ise %100'den düşük
    const efficiency = (ideal / real) * 100;
    return Math.min(120, Math.max(0, Math.round(efficiency))); // Max %120, min %0
  };

  // Kart tanımları
  const cards = [
    { id: 'totalParkArea', label: 'Toplam Park Alanı', key: 'totalParkArea' },
    { id: 'totalWaterConsumption', label: 'Toplam Su Tüketimi', key: 'totalWaterConsumption' },
    { id: 'totalGreenArea', label: 'Toplam Yeşil Alan', key: 'totalGreenArea' },
    { id: 'greenAreaWaterConsumption', label: 'Yeşil Alan Su Tüketimi', key: 'greenAreaWaterConsumption' },
    { id: 'population', label: 'Ankara Nüfusu', key: 'population' },
    { id: 'efficiency', label: 'Verimlilik', key: 'efficiency' },
  ];

  // Belediye seçimi toggle
  const toggleBelediye = (belediyeId) => {
    if (selectedBelediyeler.includes(belediyeId)) {
      setSelectedBelediyeler(selectedBelediyeler.filter(id => id !== belediyeId));
    } else {
      setSelectedBelediyeler([...selectedBelediyeler, belediyeId]);
    }
  };

  // Grafik için ilçe seçimi toggle
  const toggleIlceForChart = (belediyeId) => {
    if (selectedIlcelerForChart.includes(belediyeId)) {
      setSelectedIlcelerForChart(selectedIlcelerForChart.filter(id => id !== belediyeId));
    } else {
      setSelectedIlcelerForChart([...selectedIlcelerForChart, belediyeId]);
    }
  };

  // Filtreleme paneli açma/kapama
  const toggleFilterPanel = (panelKey) => {
    setFilterPanelsOpen(prev => ({
      ...prev,
      [panelKey]: !prev[panelKey]
    }));
  };

  // Grafik filtreleme fonksiyonları
  const toggleChartBelediye = (chartKey, belediyeId) => {
    setChartFilters(prev => ({
      ...prev,
      [chartKey]: {
        ...prev[chartKey],
        belediyeler: prev[chartKey].belediyeler.includes(belediyeId)
          ? prev[chartKey].belediyeler.filter(id => id !== belediyeId)
          : [...prev[chartKey].belediyeler, belediyeId]
      }
    }));
  };

  const toggleChartPark = (chartKey, parkId) => {
    setChartFilters(prev => ({
      ...prev,
      [chartKey]: {
        ...prev[chartKey],
        parklar: prev[chartKey].parklar.includes(parkId)
          ? prev[chartKey].parklar.filter(id => id !== parkId)
          : [...prev[chartKey].parklar, parkId]
      }
    }));
  };

  // Tablo filtreleme fonksiyonları
  const toggleTableBelediye = (tableKey, belediyeId) => {
    setTableFilters(prev => ({
      ...prev,
      [tableKey]: {
        ...prev[tableKey],
        belediyeler: prev[tableKey].belediyeler.includes(belediyeId)
          ? prev[tableKey].belediyeler.filter(id => id !== belediyeId)
          : [...prev[tableKey].belediyeler, belediyeId]
      }
    }));
  };

  const toggleTablePark = (tableKey, parkId) => {
    setTableFilters(prev => ({
      ...prev,
      [tableKey]: {
        ...prev[tableKey],
        parklar: prev[tableKey].parklar.includes(parkId)
          ? prev[tableKey].parklar.filter(id => id !== parkId)
          : [...prev[tableKey].parklar, parkId]
      }
    }));
  };

  // Aktif karta göre grafik verileri - Çeyreklik bazlı (Filtreleme ile)
  const getChartData = () => {
    // Filtreleme uygula
    const filteredBelediyeler = chartFilters.mainChart.belediyeler.length > 0
      ? mockData.belediyeler.filter(b => chartFilters.mainChart.belediyeler.includes(b.id))
      : mockData.belediyeler;
    
    // Park filtreleme varsa, belediye verilerini park bazlı filtrele
    const hasParkFilter = chartFilters.mainChart.parklar.length > 0;
    
    switch (activeCard) {
      case 'totalParkArea':
        let parkAreaData = mockData.yearlyTrend.map(d => {
          let value = d.area;
          // Filtreleme uygula
          if (hasParkFilter) {
            // Seçilen parkların alanlarını topla
            const selectedParksArea = filteredBelediyeler.reduce((sum, belediye) => {
              const parks = mockData.parks[belediye.id] || [];
              const filteredParks = parks.filter(p => chartFilters.mainChart.parklar.includes(p.id));
              return sum + filteredParks.reduce((pSum, p) => pSum + p.area, 0);
            }, 0);
            // Oran hesapla
            const totalParksArea = mockData.belediyeler.reduce((sum, belediye) => {
              const parks = mockData.parks[belediye.id] || [];
              return sum + parks.reduce((pSum, p) => pSum + p.area, 0);
            }, 0);
            if (totalParksArea > 0) {
              value = (d.area * selectedParksArea) / totalParksArea;
            }
          } else if (filteredBelediyeler.length < mockData.belediyeler.length && filteredBelediyeler.length > 0) {
            // Sadece belediye filtresi varsa - Seçilen belediyelerin park alanlarını topla
            const filteredTotalParkArea = filteredBelediyeler.reduce((sum, b) => sum + b.parkArea, 0);
            const totalParkArea = mockData.belediyeler.reduce((sum, b) => sum + b.parkArea, 0);
            if (totalParkArea > 0) {
              value = (d.area * filteredTotalParkArea) / totalParkArea;
            }
          }
          return { period: d.period, value };
        });
        return {
          title: 'Toplam Park Alanı (m²)',
          data: parkAreaData,
          type: 'bar',
        };
      case 'totalWaterConsumption':
        let consumptionData = mockData.yearlyTrend.map(d => {
          let value = d.consumption;
          // Filtreleme uygula
          if (hasParkFilter) {
            const selectedParksConsumption = filteredBelediyeler.reduce((sum, belediye) => {
              const parks = mockData.parks[belediye.id] || [];
              const filteredParks = parks.filter(p => chartFilters.mainChart.parklar.includes(p.id));
              return sum + filteredParks.reduce((pSum, p) => pSum + p.waterConsumption, 0);
            }, 0);
            const totalParksConsumption = mockData.belediyeler.reduce((sum, belediye) => {
              const parks = mockData.parks[belediye.id] || [];
              return sum + parks.reduce((pSum, p) => pSum + p.waterConsumption, 0);
            }, 0);
            if (totalParksConsumption > 0) {
              value = (d.consumption * selectedParksConsumption) / totalParksConsumption;
            }
          } else if (filteredBelediyeler.length < mockData.belediyeler.length && filteredBelediyeler.length > 0) {
            // Sadece belediye filtresi varsa - Seçilen belediyelerin su tüketimini topla
            const filteredTotalConsumption = filteredBelediyeler.reduce((sum, b) => sum + b.waterConsumption, 0);
            const totalConsumption = mockData.belediyeler.reduce((sum, b) => sum + b.waterConsumption, 0);
            if (totalConsumption > 0) {
              value = (d.consumption * filteredTotalConsumption) / totalConsumption;
            }
          }
          return { 
            period: d.period, 
            value,
            population: d.population,
            consumptionPerPerson: d.population > 0 ? (value * 1000) / d.population : 0,
          };
        });
        return {
          title: 'Toplam Su Tüketimi (m³) - Nüfus Analizi',
          data: consumptionData,
          type: 'line',
        };
      case 'totalGreenArea':
        let greenAreaData = mockData.yearlyTrend.map(d => {
          let value = d.area * 0.9;
          if (hasParkFilter) {
            const selectedParksGreenArea = filteredBelediyeler.reduce((sum, belediye) => {
              const parks = mockData.parks[belediye.id] || [];
              const filteredParks = parks.filter(p => chartFilters.mainChart.parklar.includes(p.id));
              return sum + filteredParks.reduce((pSum, p) => pSum + p.greenArea, 0);
            }, 0);
            const totalParksGreenArea = mockData.belediyeler.reduce((sum, belediye) => {
              const parks = mockData.parks[belediye.id] || [];
              return sum + parks.reduce((pSum, p) => pSum + p.greenArea, 0);
            }, 0);
            if (totalParksGreenArea > 0) {
              value = (d.area * 0.9 * selectedParksGreenArea) / totalParksGreenArea;
            }
          } else if (filteredBelediyeler.length < mockData.belediyeler.length && filteredBelediyeler.length > 0) {
            // Sadece belediye filtresi varsa - Seçilen belediyelerin yeşil alanlarını topla
            const filteredTotalGreenArea = filteredBelediyeler.reduce((sum, b) => sum + b.greenArea, 0);
            const totalGreenArea = mockData.belediyeler.reduce((sum, b) => sum + b.greenArea, 0);
            if (totalGreenArea > 0) {
              value = (d.area * 0.9 * filteredTotalGreenArea) / totalGreenArea;
            }
          }
          return { period: d.period, value };
        });
        return {
          title: 'Toplam Yeşil Alan (m²)',
          data: greenAreaData,
          type: 'area',
        };
      case 'greenAreaWaterConsumption':
        let greenConsumptionData = mockData.yearlyTrend.map(d => {
          let consumption = d.consumption;
          let area = d.area * 0.9;
          if (hasParkFilter) {
            const selectedParksData = filteredBelediyeler.reduce((acc, belediye) => {
              const parks = mockData.parks[belediye.id] || [];
              const filteredParks = parks.filter(p => chartFilters.mainChart.parklar.includes(p.id));
              return {
                consumption: acc.consumption + filteredParks.reduce((sum, p) => sum + p.waterConsumption, 0),
                area: acc.area + filteredParks.reduce((sum, p) => sum + p.greenArea, 0)
              };
            }, { consumption: 0, area: 0 });
            consumption = selectedParksData.consumption;
            area = selectedParksData.area;
          } else if (filteredBelediyeler.length < mockData.belediyeler.length && filteredBelediyeler.length > 0) {
            // Sadece belediye filtresi varsa - Seçilen belediyelerin verilerini kullan
            const filteredConsumption = filteredBelediyeler.reduce((sum, b) => sum + b.waterConsumption, 0);
            const filteredArea = filteredBelediyeler.reduce((sum, b) => sum + b.greenArea, 0);
            const totalConsumption = mockData.belediyeler.reduce((sum, b) => sum + b.waterConsumption, 0);
            const totalArea = mockData.belediyeler.reduce((sum, b) => sum + b.greenArea, 0);
            if (totalConsumption > 0 && totalArea > 0) {
              consumption = (d.consumption * filteredConsumption) / totalConsumption;
              area = (d.area * 0.9 * filteredArea) / totalArea;
            }
          }
          return { 
            period: d.period, 
            value: area > 0 ? parseFloat((consumption / area).toFixed(2)) : 0
          };
        });
        return {
          title: 'Yeşil Alan Su Tüketimi (m³/m²)',
          data: greenConsumptionData,
          type: 'line',
        };
      case 'population':
        // Ankara nüfusu için filtreleme - seçilen belediyelerin nüfuslarını topla
        let populationData = mockData.yearlyTrend.map(d => {
          let consumption = d.consumption;
          let population = d.population;
          
          // Belediye filtresi varsa
          if (filteredBelediyeler.length < mockData.belediyeler.length) {
            const filteredNufus = filteredBelediyeler.reduce((sum, b) => sum + b.nufus, 0);
            const totalNufus = mockData.belediyeler.reduce((sum, b) => sum + b.nufus, 0);
            const filteredRatio = filteredNufus / totalNufus;
            // Ankara nüfusu için oran uygula
            const ankaraNufus = 5663322; // Ankara toplam nüfusu
            population = ankaraNufus * filteredRatio;
            
            const filteredConsumption = filteredBelediyeler.reduce((sum, b) => sum + b.waterConsumption, 0);
            const totalConsumption = mockData.belediyeler.reduce((sum, b) => sum + b.waterConsumption, 0);
            consumption = d.consumption * (filteredConsumption / totalConsumption);
          } else {
            // Filtre yoksa Ankara nüfusu göster
            const ankaraNufus = 5663322;
            population = ankaraNufus;
          }
          
          // Park filtresi varsa
          if (hasParkFilter) {
            const selectedParksConsumption = filteredBelediyeler.reduce((sum, belediye) => {
              const parks = mockData.parks[belediye.id] || [];
              const filteredParks = parks.filter(p => chartFilters.mainChart.parklar.includes(p.id));
              return sum + filteredParks.reduce((pSum, p) => pSum + p.waterConsumption, 0);
            }, 0);
            const totalParksConsumption = filteredBelediyeler.reduce((sum, belediye) => {
              const parks = mockData.parks[belediye.id] || [];
              return sum + parks.reduce((pSum, p) => pSum + p.waterConsumption, 0);
            }, 0);
            if (totalParksConsumption > 0) {
              consumption = (d.consumption * selectedParksConsumption) / totalParksConsumption;
            }
          }
          
          return {
            period: d.period,
            consumption,
            population,
            consumptionPerPerson: population > 0 ? (consumption * 1000) / population : 0,
            populationNormalized: population / 1000000, // Milyon cinsinden
            consumptionNormalized: consumption / 1000000, // Milyon m³ cinsinden
          };
        });
        return {
          title: 'Ankara Nüfusu ve Su Tüketimi Analizi',
          data: populationData,
          type: 'line',
        };
      case 'efficiency':
        return {
          title: 'Park Verimlilik Ortalaması (%)',
          data: mockData.yearlyTrend.map((d, i) => {
            // Verimlilik: Park verilerinden hesaplanan ortalama
            const allParks = getAllParks();
            let baseEfficiency = allParks.length > 0 
              ? allParks.reduce((sum, p) => sum + getParkEfficiency(p), 0) / allParks.length
              : 75 + (i * 1.2);
            
            // Verimlilik anomalileri ekle
            const year = parseInt(d.period.split('/')[0]);
            const quarter = parseInt(d.period.split('Q')[1]);
            
            if (year === 2016 && quarter === 3) {
              baseEfficiency *= 0.92; // Kuraklık - verimlilik düşer
            } else if (year === 2017 && quarter === 1) {
              baseEfficiency *= 1.08; // İyileştirme programı başarılı
            } else if (year === 2018 && quarter === 2) {
              baseEfficiency *= 0.88; // Sıcak yaz - verimlilik düşer
            } else if (year === 2019 && quarter === 1) {
              baseEfficiency *= 1.12; // Yeni teknoloji entegrasyonu
            } else if (year === 2020 && quarter === 2) {
              baseEfficiency *= 1.15; // Pandemi - daha az kullanım, verimlilik artar
            } else if (year === 2020 && quarter === 4) {
              baseEfficiency *= 0.95; // Kış etkisi
            } else if (year === 2021 && quarter === 2) {
              baseEfficiency *= 0.90; // Normale dönüş - verimlilik biraz düşer
            } else if (year === 2022 && quarter === 1) {
              baseEfficiency *= 1.10; // Yeni sistemler devreye girer
            } else if (year === 2022 && quarter === 3) {
              baseEfficiency *= 0.93; // Yaz artışı - verimlilik düşer
            } else if (year === 2023 && quarter === 2) {
              baseEfficiency *= 1.08; // İyileştirme programı
            } else if (year === 2024 && quarter === 1) {
              baseEfficiency *= 0.97; // Kış etkisi
            } else if (year === 2024 && quarter === 3) {
              baseEfficiency *= 0.91; // Sıcak yaz - verimlilik düşer
            } else if (year === 2025 && quarter === 1) {
              baseEfficiency *= 1.05; // Yeni yıl iyileştirmeleri
            }
            
            // Mevsimsel ve rastgele dalgalanmalar
            const seasonalVariation = Math.sin((quarter - 1) * Math.PI / 2) * 0.05;
            const randomVariation = 0.96 + (Math.random() * 0.08);
            baseEfficiency *= (1 + seasonalVariation) * randomVariation;
            
            return { 
              period: d.period, 
              value: Math.max(60, Math.min(120, Math.round(baseEfficiency)))
            };
          }),
          type: 'line',
        };
      default:
        return null;
    }
  };

  // İL/İLÇE TÜKETİM ÖZETİ sayfası
  const renderIlIlceOzet = () => {
    const filteredBelediyeler = selectedBelediyeler.length > 0
      ? mockData.belediyeler.filter(b => selectedBelediyeler.includes(b.id))
      : mockData.belediyeler;

    return (
      <div className={styles.pageContent}>
        <div className={styles.filterSection}>
          <h3>Belediye Filtreleme</h3>
          <p className={styles.filterDescription}>
            Hangi belediyelerin özet bilgisini görmek istediğinizi seçin
          </p>
          <div className={styles.checkboxGroup}>
            {mockData.belediyeler.map(belediye => (
              <label key={belediye.id} className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={selectedBelediyeler.includes(belediye.id)}
                  onChange={() => toggleBelediye(belediye.id)}
                />
                <span>{belediye.name}</span>
              </label>
            ))}
          </div>
          {selectedBelediyeler.length > 0 && (
            <div className={styles.filterInfo}>
              {selectedBelediyeler.length} belediye seçildi
            </div>
          )}
        </div>

        <div className={styles.tableContainer}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Belediyeler</th>
                <th>Nüfus</th>
                <th>Toplam Park Sayısı</th>
                <th>Toplam Park Alanı (m²)</th>
                <th>Yeşil Alan (m²)</th>
                <th>Toplam Su Tüketimi (m³)</th>
                <th>Yeşil Alan Su Tüketimi (m³/park)</th>
                <th>Yeşil Alan Su Tüketimi (m³/m²)</th>
              </tr>
            </thead>
            <tbody>
              {filteredBelediyeler.map(belediye => {
                const waterPerPark = belediye.waterConsumption / belediye.parks;
                const waterPerM2 = belediye.waterConsumption / belediye.greenArea;
                return (
                  <tr key={belediye.id}>
                    <td>{belediye.name}</td>
                    <td>{belediye.nufus.toLocaleString('tr-TR')}</td>
                    <td>{belediye.parks}</td>
                    <td>{belediye.parkArea.toLocaleString('tr-TR')}</td>
                    <td>{belediye.greenArea.toLocaleString('tr-TR')}</td>
                    <td>{(belediye.waterConsumption / 1000).toFixed(1)}K</td>
                    <td>{(waterPerPark / 1000).toFixed(2)}K</td>
                    <td>{waterPerM2.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Tüm parkları topla (PARK sıralamaları için)
  const getAllParks = () => {
    const allParks = [];
    mockData.belediyeler.forEach(belediye => {
      const parks = mockData.parks[belediye.id] || [];
      parks.forEach(park => {
        allParks.push({
          ...park,
          belediyeName: belediye.name,
          belediyeId: belediye.id,
        });
      });
    });
    return allParks;
  };

  // PARK sıralamaları hesapla - Verimlilik bazlı
  const getParkRankings = () => {
    const allParks = getAllParks();
    // Her park için verimlilik hesapla
    const parksWithEfficiency = allParks.map(park => ({
      ...park,
      calculatedEfficiency: getParkEfficiency(park),
    }));
    const sortedByEfficiency = [...parksWithEfficiency].sort((a, b) => 
      b.calculatedEfficiency - a.calculatedEfficiency
    );
    
    return {
      park250: sortedByEfficiency.slice(0, 250),
      park100: sortedByEfficiency.slice(0, 100),
      park50: sortedByEfficiency.slice(0, 50),
      park30: sortedByEfficiency.slice(0, 30),
    };
  };

  // Harita başlatma
  useEffect(() => {
    if (showMap && mapContainerRef.current && !mapRef.current && maplibregl) {
      mapRef.current = new maplibregl.Map({
        container: mapContainerRef.current,
        style: `https://api.maptiler.com/maps/basic-v2/style.json?key=${mapTilerKey}`,
        center: [32.8597, 39.9334], // Ankara merkez
        zoom: 11,
      });
      
      mapRef.current.addControl(new maplibregl.NavigationControl(), 'top-right');
      
      mapRef.current.on('load', () => {
        // Seçili belediyenin parklarını göster
        if (selectedBelediye) {
          const parks = mockData.parks[selectedBelediye] || [];
          showParksOnMap(parks, selectedBelediye);
        }
      });
    }
    
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [showMap]);

  // Belediye değiştiğinde veya harita açıldığında parkları güncelle
  useEffect(() => {
    if (showMap && mapRef.current && mapRef.current.loaded() && selectedBelediye) {
      const parks = mockData.parks[selectedBelediye] || [];
      showParksOnMap(parks, selectedBelediye);
    }
  }, [selectedBelediye, showMap]);

  // Parkları haritada göster
  const showParksOnMap = (parks, belediyeId) => {
    if (!mapRef.current || parks.length === 0) return;
    
    const belediye = mockData.belediyeler.find(b => b.id === belediyeId);
    if (!belediye) return;
    
    // Belediye bazlı koordinat merkezleri (örnek)
    const belediyeCenters = {
      1: { lng: 32.8597, lat: 39.9334 }, // Çankaya - Ankara merkez
      2: { lng: 32.6500, lat: 39.9500 }, // Etimesgut
      3: { lng: 32.8000, lat: 39.9000 }, // Yenimahalle
      4: { lng: 32.8500, lat: 39.9200 }, // Büyükşehir
    };
    
    const center = belediyeCenters[belediyeId] || { lng: 32.8597, lat: 39.9334 };
    
    const features = parks.map((park, idx) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [
          center.lng + (Math.random() - 0.5) * 0.05, // Belediye merkezine yakın
          center.lat + (Math.random() - 0.5) * 0.05,
        ],
      },
      properties: {
        name: park.name,
        id: park.id,
        area: park.area,
        greenArea: park.greenArea,
        waterConsumption: park.waterConsumption,
        efficiency: getParkEfficiency(park),
        belediyeName: belediye.name,
      },
    }));
    
    // Eski layer'ları temizle
    if (mapRef.current.getLayer('parks-layer')) {
      mapRef.current.removeLayer('parks-layer');
    }
    if (mapRef.current.getSource('parks-layer')) {
      mapRef.current.removeSource('parks-layer');
    }
    
    // Yeni source ve layer ekle
    mapRef.current.addSource('parks-layer', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: features,
      },
    });
    
    mapRef.current.addLayer({
      id: 'parks-layer',
      type: 'circle',
      source: 'parks-layer',
      paint: {
        'circle-radius': 8,
        'circle-color': '#3b82f6',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });
    
    // Click event ekle
    mapRef.current.on('click', 'parks-layer', (e) => {
      const park = e.features[0].properties;
      const coordinates = e.features[0].geometry.coordinates.slice();
      
      // Popup içeriği
      const popupContent = `
        <div style="padding: 12px; min-width: 200px;">
          <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600; color: #1e293b;">
            ${park.name}
          </h3>
          <div style="font-size: 13px; color: #64748b; line-height: 1.6;">
            <div><strong>Belediye:</strong> ${park.belediyeName}</div>
            <div><strong>Toplam Alan:</strong> ${park.area.toLocaleString('tr-TR')} m²</div>
            <div><strong>Yeşil Alan:</strong> ${park.greenArea.toLocaleString('tr-TR')} m²</div>
            <div><strong>Su Tüketimi:</strong> ${(park.waterConsumption / 1000).toFixed(1)}K m³</div>
            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e2e8f0;">
              <strong>Verimlilik:</strong> 
              <span style="color: ${park.efficiency >= 85 ? '#10b981' : park.efficiency >= 70 ? '#f59e0b' : '#ef4444'}; font-weight: 600;">
                ${park.efficiency}%
              </span>
            </div>
          </div>
        </div>
      `;
      
      new maplibregl.Popup()
        .setLngLat(coordinates)
        .setHTML(popupContent)
        .addTo(mapRef.current);
    });
    
    // Hover efekti
    mapRef.current.on('mouseenter', 'parks-layer', () => {
      mapRef.current.getCanvas().style.cursor = 'pointer';
    });
    
    mapRef.current.on('mouseleave', 'parks-layer', () => {
      mapRef.current.getCanvas().style.cursor = '';
    });
    
    // Tüm parkları içeren bounds hesapla
    const bounds = new maplibregl.LngLatBounds();
    features.forEach(feature => {
      bounds.extend(feature.geometry.coordinates);
    });
    
    mapRef.current.fitBounds(bounds, {
      padding: 50,
      duration: 1500,
    });
  };

  // BELEDİYE ÖZETİ sayfası
  const renderBelediyeOzet = () => {
    if (!selectedBelediye) {
      return (
        <div className={styles.pageContent}>
          <div className={styles.selectBelediyeSection}>
            <h3>Belediye Seçin</h3>
            <div className={styles.belediyeButtons}>
              {mockData.belediyeler.map(belediye => (
                <button
                  key={belediye.id}
                  className={styles.belediyeButton}
                  onClick={() => setSelectedBelediye(belediye.id)}
                >
                  {belediye.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }

    const belediye = mockData.belediyeler.find(b => b.id === selectedBelediye);
    const parks = mockData.parks[selectedBelediye] || [];
    const sortedParks = [...parks].sort((a, b) => b.waterConsumption - a.waterConsumption);
    const bestParks = sortedParks.slice(0, 5);
    const worstParks = sortedParks.slice(-5).reverse();

    // Su kaybı hesabı (örnek: ideal tüketim %80 verimlilik varsayımı)
    const idealConsumption = belediye.waterConsumption * 0.8;
    const waterLoss = belediye.waterConsumption - idealConsumption;

    // Hane/birey tüketim karşılığı (ortalama hane 3.5 kişi, günlük 200L su)
    const dailyHouseholdConsumption = 200; // litre
    const householdEquivalent = (belediye.waterConsumption * 1000) / (dailyHouseholdConsumption * 365);
    const personEquivalent = householdEquivalent * 3.5;

    // 10 yıllık trend verileri (belediye bazlı - çeyreklik)
    const totalConsumption = mockData.belediyeler.reduce((sum, b) => sum + b.waterConsumption, 0);
    const belediyeRatio = belediye.waterConsumption / totalConsumption;
    const belediyeYearlyTrend = mockData.yearlyTrend.map((period) => ({
      period: period.period,
      consumption: Math.round(period.consumption * belediyeRatio),
      population: period.population,
    }));

    return (
      <div className={styles.pageContent}>
        <div className={styles.belediyeHeader}>
          <h2>{belediye.name}</h2>
          <div className={styles.belediyeHeaderActions}>
            <button 
              className={styles.mapToggleButton}
              onClick={() => setShowMap(!showMap)}
            >
              <MapIcon size={18} />
              <span>{showMap ? 'Haritayı Gizle' : 'Haritayı Göster'}</span>
            </button>
            <button className={styles.backButton} onClick={() => {
              setSelectedBelediye(null);
              setShowMap(false);
            }}>
              ← Geri
            </button>
          </div>
        </div>

        {/* Harita Container */}
        {showMap && (
          <div className={styles.mapContainer}>
            <div ref={mapContainerRef} className={styles.map} />
            <div className={styles.mapInfo}>
              <div className={styles.mapInfoBadge}>
                <MapIcon size={16} />
                <span>{parks.length} park gösteriliyor</span>
              </div>
            </div>
          </div>
        )}

          <div className={styles.performanceSection}>
          <div className={styles.performanceCard}>
            <h3>En İyi Performans Gösteren Parklar (Top 50)</h3>
            <p className={styles.performanceSubtext}>
              Verimlilik bazlı en iyi performans gösteren parklar
            </p>
            <div className={styles.parkList}>
              {bestParks.slice(0, 10).map(park => {
                const efficiency = getParkEfficiency(park);
                return (
                  <div key={park.id} className={styles.parkItem}>
                    <span className={styles.parkName}>{park.name}</span>
                    <div className={styles.parkDetails}>
                      <span className={styles.parkValue}>
                        {(park.waterConsumption / 1000).toFixed(1)}K m³
                      </span>
                      <span className={styles.parkEfficiency} style={{
                        color: efficiency >= 85 ? '#10b981' : efficiency >= 70 ? '#f59e0b' : '#ef4444'
                      }}>
                        {efficiency}% verimli
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            {bestParks.length > 10 && (
              <div className={styles.moreParksInfo}>
                +{bestParks.length - 10} park daha
              </div>
            )}
          </div>

          <div className={styles.performanceCard}>
            <h3>En Kötü Performans Gösteren Parklar (Son 50)</h3>
            <p className={styles.performanceSubtext}>
              Verimlilik bazlı en kötü performans gösteren parklar
            </p>
            <div className={styles.parkList}>
              {worstParks.slice(0, 10).map(park => {
                const efficiency = getParkEfficiency(park);
                return (
                  <div key={park.id} className={styles.parkItem}>
                    <span className={styles.parkName}>{park.name}</span>
                    <div className={styles.parkDetails}>
                      <span className={styles.parkValue}>
                        {(park.waterConsumption / 1000).toFixed(1)}K m³
                      </span>
                      <span className={styles.parkEfficiency} style={{
                        color: efficiency >= 85 ? '#10b981' : efficiency >= 70 ? '#f59e0b' : '#ef4444'
                      }}>
                        {efficiency}% verimli
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            {worstParks.length > 10 && (
              <div className={styles.moreParksInfo}>
                +{worstParks.length - 10} park daha
              </div>
            )}
          </div>
        </div>

        <div className={styles.tableContainer}>
          <div className={styles.tableHeader}>
            <h3>Park Detayları (m² yeşil alan bazlı sıralama)</h3>
            <div className={styles.tableHeaderActions}>
              <div className={styles.tableInfo}>
                Toplam {parks.length} park gösteriliyor
              </div>
              <button
                className={styles.filterToggleButton}
                onClick={() => toggleFilterPanel('belediyeTable')}
                title={filterPanelsOpen.belediyeTable ? 'Filtreleri Gizle' : 'Filtreleri Göster'}
              >
                <Filter size={18} />
                {filterPanelsOpen.belediyeTable ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>
          </div>
          
          {/* Tablo Filtreleme - Tablonun Altında */}
          {filterPanelsOpen.belediyeTable && (
            <div className={styles.tableFilterSection}>
              <div className={styles.filterSectionHeader}>
                <h3 className={styles.filterSectionTitle}>Park Filtreleme</h3>
                <button
                  className={styles.closeFilterButton}
                  onClick={() => toggleFilterPanel('belediyeTable')}
                  title="Filtreleri Kapat"
                >
                  <X size={18} />
                </button>
              </div>
              <div className={styles.parkCheckboxList} style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {parks.map(park => (
                  <label key={park.id} className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={tableFilters.belediyeTable.parklar.includes(park.id)}
                      onChange={() => toggleTablePark('belediyeTable', park.id)}
                    />
                    <span>{park.name}</span>
                  </label>
                ))}
              </div>
              {tableFilters.belediyeTable.parklar.length > 0 && (
                <div className={styles.filterInfo}>
                  {tableFilters.belediyeTable.parklar.length} park seçildi
                </div>
              )}
            </div>
          )}
          
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Park</th>
                <th>Toplam Park Alanı (m²)</th>
                <th>Yeşil Alan (m²)</th>
                <th>Toplam Su Tüketimi (m³)</th>
                <th>Yeşil Alan Su Tüketimi (m³/park)</th>
                <th>Yeşil Alan Su Tüketimi (m³/m²)</th>
                <th>Verimlilik (%)</th>
              </tr>
            </thead>
            <tbody>
              {[...parks]
                .filter(park => tableFilters.belediyeTable.parklar.length === 0 || tableFilters.belediyeTable.parklar.includes(park.id))
                .sort((a, b) => b.greenArea - a.greenArea)
                .map(park => {
                const waterPerM2 = park.waterConsumption / park.greenArea;
                const efficiency = getParkEfficiency(park);
                return (
                  <tr key={park.id}>
                    <td>{park.name}</td>
                    <td>{park.area.toLocaleString('tr-TR')}</td>
                    <td>{park.greenArea.toLocaleString('tr-TR')}</td>
                    <td>{(park.waterConsumption / 1000).toFixed(1)}K</td>
                    <td>{(park.waterConsumption / 1000).toFixed(2)}K</td>
                    <td>{waterPerM2.toFixed(2)}</td>
                    <td>
                      <div className={styles.efficiencyBar}>
                        <div
                          className={styles.efficiencyFill}
                          style={{
                            width: `${efficiency}%`,
                            backgroundColor: efficiency >= 85 ? '#10b981' : efficiency >= 70 ? '#f59e0b' : '#ef4444',
                          }}
                        />
                      </div>
                      <span className={styles.efficiencyText}>{efficiency}%</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className={styles.statsSection}>
          <div className={styles.statCard}>
            <h4>İlçe Nüfusu</h4>
            <p>{belediye.nufus.toLocaleString('tr-TR')} kişi</p>
            <p className={styles.statSubtext}>
              {((belediye.parkArea / belediye.nufus) * 1000).toFixed(2)} m² park / 1000 kişi
            </p>
            <p className={styles.statSubtext}>
              {((belediye.greenArea / belediye.nufus) * 1000).toFixed(2)} m² yeşil alan / 1000 kişi
            </p>
          </div>
          <div className={styles.statCard}>
            <h4>Su Tüketim Karşılıkları</h4>
            <p className={styles.statSubtext}>
              Hane Karşılığı: {householdEquivalent.toFixed(0)} hane/yıl
            </p>
            <p className={styles.statSubtext}>
              Birey Karşılığı: {personEquivalent.toFixed(0)} kişi/yıl
            </p>
            <p className={styles.statSubtext} style={{ color: '#ef4444', fontWeight: 600 }}>
              Su Kaybı: {(waterLoss / 1000).toFixed(1)}K m³/yıl
            </p>
          </div>
        </div>

        <div className={styles.chartSection}>
          <div className={styles.chartHeader}>
            <h3>Son 10 Yılda Su Tüketim Trendi (Çeyreklik)</h3>
            
            {/* Belediye Özeti Grafik Filtreleme */}
            <div className={styles.chartFilterSection}>
              <div className={styles.filterGroup}>
                <h4 className={styles.filterGroupTitle}>Park Filtreleme (Grafik İçin)</h4>
                <div className={styles.parkCheckboxList} style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {parks.slice(0, 30).map(park => (
                    <label key={park.id} className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={chartFilters.belediyeChart.parklar.includes(park.id)}
                        onChange={() => toggleChartPark('belediyeChart', park.id)}
                      />
                      <span>{park.name}</span>
                    </label>
                  ))}
                  {parks.length > 30 && (
                    <div className={styles.moreParksInfo}>
                      +{parks.length - 30} park daha
                    </div>
                  )}
                </div>
                {chartFilters.belediyeChart.parklar.length > 0 && (
                  <div className={styles.filterInfo}>
                    {chartFilters.belediyeChart.parklar.length} park seçildi
                  </div>
                )}
              </div>
            </div>
            
            <div className={styles.comparisonControls}>
              <label>Karşılaştırma:</label>
              <select 
                value={comparisonCount}
                onChange={(e) => setComparisonCount(parseInt(e.target.value))}
                className={styles.comparisonSelect}
              >
                <option value={1}>Sadece {belediye.name}</option>
                <option value={3}>3 Belediye</option>
                <option value={5}>5 Belediye</option>
                <option value={mockData.belediyeler.length}>Tüm Belediyeler</option>
              </select>
            </div>
          </div>
          
          {/* Belediye Özeti Grafik Filtreleme - Grafiğin Altında */}
          {filterPanelsOpen.belediyeChart && (
            <div className={styles.chartFilterSection}>
              <div className={styles.filterSectionHeader}>
                <h3 className={styles.filterSectionTitle}>Park Filtreleme</h3>
                <button
                  className={styles.closeFilterButton}
                  onClick={() => toggleFilterPanel('belediyeChart')}
                  title="Filtreleri Kapat"
                >
                  <X size={18} />
                </button>
              </div>
              <div className={styles.filterGroup}>
                <div className={styles.parkCheckboxList} style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {parks.slice(0, 50).map(park => (
                    <label key={park.id} className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={chartFilters.belediyeChart.parklar.includes(park.id)}
                        onChange={() => toggleChartPark('belediyeChart', park.id)}
                      />
                      <span>{park.name}</span>
                    </label>
                  ))}
                  {parks.length > 50 && (
                    <div className={styles.moreParksInfo}>
                      +{parks.length - 50} park daha
                    </div>
                  )}
                </div>
                {chartFilters.belediyeChart.parklar.length > 0 && (
                  <div className={styles.filterInfo}>
                    {chartFilters.belediyeChart.parklar.length} park seçildi
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className={styles.chartWrapper}>
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={(() => {
                // Seçilen belediye sayısına göre veri hazırla
                const selectedForComparison = mockData.belediyeler
                  .slice(0, comparisonCount)
                  .map(b => {
                    const ratio = b.waterConsumption / mockData.belediyeler.reduce((sum, bel) => sum + bel.waterConsumption, 0);
                    return {
                      name: b.name,
                      data: mockData.yearlyTrend.map(p => ({
                        period: p.period,
                        consumption: Math.round(p.consumption * ratio),
                      })),
                    };
                  });
                
                // Tüm dönemleri birleştir
                const periods = mockData.yearlyTrend.map(p => p.period);
                return periods.map(period => {
                  const dataPoint = { period };
                  selectedForComparison.forEach((belediye, idx) => {
                    const point = belediye.data.find(d => d.period === period);
                    dataPoint[belediye.name] = point ? point.consumption : 0;
                  });
                  return dataPoint;
                });
              })()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="period" 
                  angle={-45} 
                  textAnchor="end" 
                  height={100}
                  interval={7}
                />
                <YAxis />
                <Tooltip 
                  formatter={(value) => `${(value / 1000).toFixed(1)}K m³`}
                  labelFormatter={(label) => `Dönem: ${label}`}
                />
                <Legend />
                {mockData.belediyeler.slice(0, comparisonCount).map((belediye, idx) => {
                  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];
                  return (
                    <Area 
                      key={belediye.id}
                      type="monotone" 
                      dataKey={belediye.name} 
                      stroke={colors[idx % colors.length]} 
                      fill={colors[idx % colors.length]} 
                      fillOpacity={0.3} 
                      dot={{ r: 3 }}
                      name={belediye.name}
                    />
                  );
                })}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Teorik vs Gerçek Karşılaştırması */}
        <div className={styles.chartSection}>
          <h3>Teorik vs Gerçek Su Tüketim Karşılaştırması</h3>
          <div className={styles.comparisonLegend}>
            <div className={styles.legendItem}>
              <div className={styles.legendColor} style={{ background: '#10b981' }}></div>
              <span>Teorik Tüketim</span>
            </div>
            <div className={styles.legendItem}>
              <div className={styles.legendColor} style={{ background: '#3b82f6' }}></div>
              <span>Gerçek Tüketim</span>
            </div>
          </div>
          <div className={styles.chartWrapper}>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={parks.map(park => {
                const theoretical = park.waterConsumption * 0.9;
                const real = park.waterConsumption;
                return {
                  name: park.name,
                  teorik: theoretical,
                  gerçek: real,
                };
              }).slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip formatter={(value) => `${(value / 1000).toFixed(1)}K m³`} />
                <Legend />
                <Bar dataKey="teorik" fill="#10b981" name="Teorik" />
                <Bar dataKey="gerçek" fill="#3b82f6" name="Gerçek" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* PARK Sıralamaları */}
        <div className={styles.parkRankingsSection}>
          <h3>PARK Sıralamaları (Verimlilik Bazlı)</h3>
          <div className={styles.rankingsGrid}>
            <div className={styles.rankingCard}>
              <h4>PARK 30</h4>
              <p className={styles.rankingSubtext}>En Verimli 30 Park</p>
              <div className={styles.rankingList}>
                {getParkRankings().park30.slice(0, 10).map((park, idx) => (
                  <div key={idx} className={styles.rankingItem}>
                    <span className={styles.rankingNumber}>{idx + 1}</span>
                    <span className={styles.rankingName}>{park.name} ({park.belediyeName})</span>
                    <span className={styles.rankingValue}>
                      {park.calculatedEfficiency || getParkEfficiency(park)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.rankingCard}>
              <h4>PARK 50</h4>
              <p className={styles.rankingSubtext}>En Verimli 50 Park</p>
              <div className={styles.rankingList}>
                {getParkRankings().park50.slice(0, 10).map((park, idx) => (
                  <div key={idx} className={styles.rankingItem}>
                    <span className={styles.rankingNumber}>{idx + 1}</span>
                    <span className={styles.rankingName}>{park.name} ({park.belediyeName})</span>
                    <span className={styles.rankingValue}>
                      {park.calculatedEfficiency || getParkEfficiency(park)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.rankingCard}>
              <h4>PARK 100</h4>
              <p className={styles.rankingSubtext}>En Verimli 100 Park</p>
              <div className={styles.rankingList}>
                {getParkRankings().park100.slice(0, 10).map((park, idx) => (
                  <div key={idx} className={styles.rankingItem}>
                    <span className={styles.rankingNumber}>{idx + 1}</span>
                    <span className={styles.rankingName}>{park.name} ({park.belediyeName})</span>
                    <span className={styles.rankingValue}>
                      {park.calculatedEfficiency || getParkEfficiency(park)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.rankingCard}>
              <h4>PARK 250</h4>
              <p className={styles.rankingSubtext}>En Verimli 250 Park</p>
              <div className={styles.rankingList}>
                {getParkRankings().park250.slice(0, 10).map((park, idx) => (
                  <div key={idx} className={styles.rankingItem}>
                    <span className={styles.rankingNumber}>{idx + 1}</span>
                    <span className={styles.rankingName}>{park.name} ({park.belediyeName})</span>
                    <span className={styles.rankingValue}>
                      {park.calculatedEfficiency || getParkEfficiency(park)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };


  const chartData = getChartData();

  // Raporlar sayfası
  const renderReports = () => {
    return (
      <div className={styles.pageContent}>
        <div className={styles.reportsHeader}>
          <h2>Detaylı Raporlar</h2>
          <p className={styles.reportsSubtitle}>Belediyeler için kapsamlı su tüketim ve performans raporları</p>
        </div>

        <div className={styles.reportsGrid}>
          {/* Park Sıralaması - Toplam Su Hacmi */}
          <div className={styles.reportCard}>
            <div className={styles.reportCardHeader}>
              <div className={styles.reportBadge} style={{ background: '#3b82f6' }}>RANKING</div>
              <h3>Park Sıralaması - Toplam Su Hacmi</h3>
            </div>
            <div className={styles.reportDate}>5.01.2026</div>
            <p className={styles.reportDescription}>Toplam su hacmi tüketimine göre park sıralaması</p>
            <div className={styles.reportControls}>
              <select className={styles.reportSelect}>
                <option>Top 10</option>
                <option>Top 25</option>
                <option>Top 50</option>
                <option>Top 100</option>
              </select>
              <button className={styles.reportButton}>PDF İndir</button>
            </div>
          </div>

          {/* Park Sıralaması - L/m² */}
          <div className={styles.reportCard}>
            <div className={styles.reportCardHeader}>
              <div className={styles.reportBadge} style={{ background: '#10b981' }}>RANKING</div>
              <h3>Park Sıralaması - L/m²</h3>
            </div>
            <div className={styles.reportDate}>5.01.2026</div>
            <p className={styles.reportDescription}>Litre bölü metre kare cinsinden park sıralaması</p>
            <div className={styles.reportControls}>
              <select className={styles.reportSelect}>
                <option>Top 10</option>
                <option>Top 25</option>
                <option>Top 50</option>
                <option>Top 100</option>
              </select>
              <button className={styles.reportButton}>PDF İndir</button>
            </div>
          </div>

          {/* Aylık Su Tüketim Raporu */}
          <div className={styles.reportCard}>
            <div className={styles.reportCardHeader}>
              <div className={styles.reportBadge} style={{ background: '#f59e0b' }}>WATER</div>
              <h3>Aylık Su Tüketim Raporu</h3>
            </div>
            <div className={styles.reportDate}>Ocak 2025</div>
            <p className={styles.reportDescription}>Seçili parkların aylık su tüketim detayları</p>
            <div className={styles.reportControls}>
              <button className={styles.reportButtonPrimary}>Görüntüle</button>
              <button className={styles.reportButton}>PDF İndir</button>
            </div>
          </div>

          {/* Yıllık Özet Raporu */}
          <div className={styles.reportCard}>
            <div className={styles.reportCardHeader}>
              <div className={styles.reportBadge} style={{ background: '#8b5cf6' }}>SUMMARY</div>
              <h3>Yıllık Özet Raporu</h3>
            </div>
            <div className={styles.reportDate}>2024</div>
            <p className={styles.reportDescription}>Yıllık toplam tüketim ve performans özeti</p>
            <div className={styles.reportControls}>
              <button className={styles.reportButtonPrimary}>Görüntüle</button>
              <button className={styles.reportButton}>PDF İndir</button>
            </div>
          </div>

          {/* Teorik vs Gerçek Tüketim Raporu */}
          <div className={styles.reportCard}>
            <div className={styles.reportCardHeader}>
              <div className={styles.reportBadge} style={{ background: '#ef4444' }}>ANALYSIS</div>
              <h3>Teorik vs Gerçek Tüketim Analizi</h3>
            </div>
            <div className={styles.reportDate}>2025/12 (Ay)</div>
            <p className={styles.reportDescription}>Belediyeler bazında teorik ve gerçek su tüketim karşılaştırması</p>
            <div className={styles.reportControls}>
              <button className={styles.reportButtonPrimary}>Görüntüle</button>
              <button className={styles.reportButton}>PDF İndir</button>
            </div>
          </div>

          {/* Su Kaybı Analiz Raporu */}
          <div className={styles.reportCard}>
            <div className={styles.reportCardHeader}>
              <div className={styles.reportBadge} style={{ background: '#06b6d4' }}>LOSS</div>
              <h3>Su Kaybı Analiz Raporu</h3>
            </div>
            <div className={styles.reportDate}>2025/12 (Ay)</div>
            <p className={styles.reportDescription}>Belediyeler ve parklar bazında su kaybı hesaplamaları</p>
            <div className={styles.reportControls}>
              <button className={styles.reportButtonPrimary}>Görüntüle</button>
              <button className={styles.reportButton}>PDF İndir</button>
            </div>
          </div>
        </div>

        {/* Teorik vs Gerçek Grafik */}
        <div className={styles.chartSection}>
          <h3>Teorik vs Gerçek Su Tüketim Karşılaştırması</h3>
          
          {/* Raporlar Grafik Filtreleme */}
          <div className={styles.chartFilterSection}>
            <div className={styles.filterRow}>
              <div className={styles.filterGroup}>
                <h4 className={styles.filterGroupTitle}>Belediye Filtreleme</h4>
                <div className={styles.checkboxGroup} style={{ flexDirection: 'row', gap: '12px', flexWrap: 'wrap' }}>
                  {mockData.belediyeler.map(belediye => (
                    <label key={belediye.id} className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={chartFilters.reportsChart.belediyeler.includes(belediye.id)}
                        onChange={() => toggleChartBelediye('reportsChart', belediye.id)}
                      />
                      <span>{belediye.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div className={styles.chartWrapper}>
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={mockData.belediyeler
                .filter(b => chartFilters.reportsChart.belediyeler.length === 0 || chartFilters.reportsChart.belediyeler.includes(b.id))
                .map(b => ({
                  name: b.name,
                  teorik: getTheoreticalConsumption(b),
                  gerçek: getRealConsumption(b),
                  fark: getRealConsumption(b) - getTheoreticalConsumption(b),
                }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => `${(value / 1000).toFixed(1)}K m³`} />
                <Legend />
                <Bar dataKey="teorik" fill="#10b981" name="Teorik Tüketim" />
                <Bar dataKey="gerçek" fill="#3b82f6" name="Gerçek Tüketim" />
                <Line type="monotone" dataKey="fark" stroke="#ef4444" strokeWidth={2} name="Fark" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* İşletme Özeti Bölümü */}
        <div className={styles.isletmeSection}>
          <h2 style={{ marginTop: '40px', marginBottom: '20px' }}>İşletme Özeti</h2>
          <div className={styles.tableContainer}>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>Belediyeler</th>
                  <th>Sayaç Tipi (Kartlı / GPRS / Tanker)</th>
                  <th>Sulama Rejimi (Sabah / Öğlen / Akşam / Gece)</th>
                </tr>
              </thead>
              <tbody>
                {mockData.belediyeler.map(belediye => {
                  const isletme = mockData.isletme[belediye.id];
                  const totalMeters = isletme.kartli + isletme.gprs + isletme.tanker;
                  const totalIrrigation = isletme.sabah + isletme.oglen + isletme.aksam + isletme.gece;
                  return (
                    <tr key={belediye.id}>
                      <td><strong>{belediye.name}</strong></td>
                      <td>
                        <div className={styles.meterBreakdown}>
                          <span className={styles.meterItem}>
                            <strong>Kartlı:</strong> {isletme.kartli} adet
                          </span>
                          <span className={styles.meterItem}>
                            <strong>GPRS:</strong> {isletme.gprs} adet
                          </span>
                          <span className={styles.meterItem}>
                            <strong>Tanker:</strong> {isletme.tanker} adet
                          </span>
                          <span className={styles.meterTotal}>
                            <strong>Toplam:</strong> {totalMeters} adet
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className={styles.irrigationBreakdown}>
                          <span className={styles.irrigationItem}>
                            <strong>Sabah:</strong> {isletme.sabah} adet
                          </span>
                          <span className={styles.irrigationItem}>
                            <strong>Öğlen:</strong> {isletme.oglen} adet
                          </span>
                          <span className={styles.irrigationItem}>
                            <strong>Akşam:</strong> {isletme.aksam} adet
                          </span>
                          <span className={styles.irrigationItem}>
                            <strong>Gece:</strong> {isletme.gece} adet
                          </span>
                          <span className={styles.irrigationTotal}>
                            <strong>Toplam:</strong> {totalIrrigation} adet
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.dashboard}>
      <div className={styles.header}>
        <h1>ASKİ Dashboard</h1>
        <div className={styles.headerActions}>
          <button 
            className={styles.settingsButton}
            onClick={() => setShowFilters(!showFilters)}
            title="Filtreler"
          >
            <BarChart3 size={20} />
            <span>Filtreler</span>
          </button>
          <button 
            className={styles.settingsButton}
            onClick={() => setShowSettings(!showSettings)}
            title="Ayarlar"
          >
            <Settings size={20} />
            <span>Ayarlar</span>
          </button>
          <button 
            className={styles.settingsButton}
            onClick={() => {
              // Tüm filtreleme panellerini aç/kapat
              const allOpen = Object.values(filterPanelsOpen).every(v => v);
              const newState = {};
              Object.keys(filterPanelsOpen).forEach(key => {
                newState[key] = !allOpen;
              });
              setFilterPanelsOpen(newState);
            }}
            title="Tüm Filtreleri Aç/Kapat"
          >
            <Filter size={20} />
            <span>Filtreleme</span>
          </button>
        </div>
      </div>

      {/* Filtreler Paneli */}
      {showFilters && (
        <div className={styles.filtersPanel}>
          <div className={styles.filtersHeader}>
            <h3>Filtreleme ve Sıralama</h3>
            <button className={styles.closeFiltersButton} onClick={() => setShowFilters(false)}>×</button>
          </div>
          <div className={styles.filtersGrid}>
            <div>
              <label>Park Boyutu vs Su Tüketimi</label>
              <select 
                value={filters.parkSize}
                onChange={(e) => setFilters({...filters, parkSize: e.target.value})}
              >
                <option value="">Filtre Seçiniz</option>
                <option value="small">Küçük Parklar (&lt; 10,000 m²)</option>
                <option value="medium">Orta Parklar (10,000 - 50,000 m²)</option>
                <option value="large">Büyük Parklar (&gt; 50,000 m²)</option>
              </select>
            </div>
            <div>
              <label>Etrafın Nüfusu vs Su Tüketimi</label>
              <select 
                value={filters.population}
                onChange={(e) => setFilters({...filters, population: e.target.value})}
              >
                <option value="">Filtre Seçiniz</option>
                <option value="low">Düşük Nüfus (&lt; 500K)</option>
                <option value="medium">Orta Nüfus (500K - 1M)</option>
                <option value="high">Yüksek Nüfus (&gt; 1M)</option>
              </select>
            </div>
            <div>
              <label>Sulama Tipi</label>
              <div className={styles.checkboxGroup}>
                {[
                  { value: 'automatic', label: 'Otomatik Sulama' },
                  { value: 'hose', label: 'Hortum' },
                  { value: 'tanker', label: 'Tanker Sulama' },
                ].map(option => (
                  <label key={option.value} className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={filters.irrigationType.includes(option.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFilters({
                            ...filters,
                            irrigationType: [...filters.irrigationType, option.value],
                          });
                        } else {
                          setFilters({
                            ...filters,
                            irrigationType: filters.irrigationType.filter(v => v !== option.value),
                          });
                        }
                      }}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label>Abone Sayacı Tipi</label>
              <select 
                value={filters.meterType}
                onChange={(e) => setFilters({...filters, meterType: e.target.value})}
              >
                <option value="">Filtre Seçiniz</option>
                <option value="gprs">GPRS'li Sayaç</option>
                <option value="card">Kartlı Sayaç</option>
                <option value="mechanical">Mekanik Sayaç</option>
              </select>
            </div>
            <div>
              <label>Bölge</label>
              <select 
                value={filters.region}
                onChange={(e) => setFilters({...filters, region: e.target.value})}
              >
                <option value="">Tüm Bölgeler</option>
                <option value="cankaya">Çankaya</option>
                <option value="etimesgut">Etimesgut</option>
                <option value="yenimahalle">Yenimahalle</option>
                <option value="buyuksehir">Büyükşehir</option>
              </select>
            </div>
            <div>
              <label>Verimlilik Aralığı</label>
              <select 
                value={filters.efficiencyRange}
                onChange={(e) => setFilters({...filters, efficiencyRange: e.target.value})}
              >
                <option value="">Tüm Aralıklar</option>
                <option value="high">Yüksek (&gt; 85%)</option>
                <option value="medium">Orta (70-85%)</option>
                <option value="low">Düşük (&lt; 70%)</option>
              </select>
            </div>
          </div>
          <div className={styles.filtersActions}>
            <button className={styles.applyFiltersButton} onClick={() => setShowFilters(false)}>
              Filtreleri Uygula
            </button>
            <button className={styles.clearFiltersButton} onClick={() => setFilters({
              parkSize: '', population: '', irrigationType: [], meterType: '', region: '', efficiencyRange: ''
            })}>
              Filtreleri Temizle
            </button>
          </div>
        </div>
      )}

      {/* Ayarlar Paneli */}
      {showSettings && (
        <div className={styles.settingsPanel}>
          <div className={styles.settingsHeader}>
            <h3>Ayarlar</h3>
            <button className={styles.closeSettingsButton} onClick={() => setShowSettings(false)}>×</button>
          </div>
          <div className={styles.settingsContent}>
            <div className={styles.settingItem}>
              <label>Su Fiyatı (TL/L)</label>
              <input type="number" step="0.001" defaultValue="0.05" />
            </div>
            <div className={styles.settingItem}>
              <label>Varsayılan Tarih Aralığı</label>
              <select>
                <option>Son 1 Yıl</option>
                <option>Son 5 Yıl</option>
                <option>Son 10 Yıl</option>
              </select>
            </div>
            <div className={styles.settingItem}>
              <label>Grafik Tipi</label>
              <select>
                <option>Çizgi Grafik</option>
                <option>Bar Grafik</option>
                <option>Alan Grafik</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Ana Sayfa - TUİK Tarzı Kartlar */}
      {activePage === 'main' && (
        <>
          <div className={styles.cardsContainer}>
            {cards.map(card => {
              const data = mockData.summaryCards[card.key];
              const isActive = activeCard === card.id;
              const isHovered = hoveredCard === card.id;
              const IconComponent = data.icon;
              
              // Nüfus kartı için ilçe bilgileri
              const ilceInfo = card.id === 'population' ? (
                <div className={styles.ilceTooltip}>
                  <div>Çankaya: {mockData.belediyeler.find(b => b.id === 1)?.nufus.toLocaleString('tr-TR')} kişi</div>
                  <div>Etimesgut: {mockData.belediyeler.find(b => b.id === 2)?.nufus.toLocaleString('tr-TR')} kişi</div>
                  <div>Yenimahalle: {mockData.belediyeler.find(b => b.id === 3)?.nufus.toLocaleString('tr-TR')} kişi</div>
                  <div>Büyükşehir: {mockData.belediyeler.find(b => b.id === 4)?.nufus.toLocaleString('tr-TR')} kişi</div>
                </div>
              ) : null;
              
              return (
                <div
                  key={card.id}
                  className={`${styles.summaryCard} ${isActive ? styles.active : ''}`}
                  onMouseEnter={() => {
                    setHoveredCard(card.id);
                    setActiveCard(card.id);
                  }}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div className={styles.cardIcon}>
                    <IconComponent size={28} strokeWidth={1.5} />
                  </div>
                  <div className={styles.cardContent}>
                    <div className={styles.cardLabel}>{card.label}</div>
                    <div className={styles.cardValue}>
                      {typeof data.value === 'number' 
                        ? data.value.toLocaleString('tr-TR')
                        : data.value}
                      {data.unit && <span className={styles.cardUnit}> {data.unit}</span>}
                    </div>
                    <div className={styles.cardPeriod}>{data.period}</div>
                  </div>
                  {isHovered && ilceInfo && (
                    <div className={styles.hoverTooltip}>
                      {ilceInfo}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {chartData && (
            <div className={styles.chartSection}>
              <div className={styles.chartHeaderWithFilter}>
                <h2 className={styles.chartTitle}>{chartData.title}</h2>
                <button
                  className={styles.filterToggleButton}
                  onClick={() => toggleFilterPanel('mainChart')}
                  title={filterPanelsOpen.mainChart ? 'Filtreleri Gizle' : 'Filtreleri Göster'}
                >
                  <Filter size={18} />
                  {filterPanelsOpen.mainChart ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              </div>
              
              <div className={styles.comparisonLegend}>
                <div className={styles.legendItem}>
                  <div className={styles.legendColor} style={{ background: '#3b82f6' }}></div>
                  <span>Gerçek</span>
                </div>
                <div className={styles.legendItem}>
                  <div className={styles.legendColor} style={{ background: '#10b981', opacity: 0.8 }}></div>
                  <span>Teorik Hesaplama</span>
                </div>
                {activeCard === 'totalWaterConsumption' && selectedIlcelerForChart.length > 0 && selectedIlcelerForChart.map((belediyeId) => {
                  const belediye = mockData.belediyeler.find(b => b.id === belediyeId);
                  if (!belediye) return null;
                  const colors = ['#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];
                  const idx = selectedIlcelerForChart.indexOf(belediyeId);
                  return (
                    <div key={belediyeId} className={styles.legendItem}>
                      <div className={styles.legendColor} style={{ background: colors[idx % colors.length] }}></div>
                      <span>{belediye.name}</span>
                    </div>
                  );
                })}
                {chartFilters.mainChart.belediyeler.length > 0 && (
                  <div className={styles.legendItem}>
                    <div className={styles.legendColor} style={{ background: '#64748b' }}></div>
                    <span>Filtrelenmiş Veri ({chartFilters.mainChart.belediyeler.length} ilçe)</span>
                  </div>
                )}
              </div>
              <div className={styles.chartWrapper}>
                <ResponsiveContainer width="100%" height={400}>
                  {chartData.type === 'line' ? (
                    <LineChart 
                      data={chartData.data.map(d => {
                        const base = {
                          ...d,
                          teorik: typeof d.value === 'number' ? d.value * 0.9 : d.value,
                        };
                        // Population kartı için normalize edilmiş değerler ekle
                        if (activeCard === 'population' && d.consumption && d.population) {
                          base.consumptionNormalized = d.consumption / 1000000;
                          base.populationNormalized = d.population / 1000000;
                        }
                        // Seçilen ilçelerin tüketimlerini ekle
                        if (activeCard === 'totalWaterConsumption') {
                          const totalConsumption = mockData.belediyeler.reduce((sum, b) => sum + b.waterConsumption, 0);
                          selectedIlcelerForChart.forEach(belediyeId => {
                            const belediye = mockData.belediyeler.find(b => b.id === belediyeId);
                            if (belediye) {
                              const belediyeRatio = belediye.waterConsumption / totalConsumption;
                              // Dönem bazlı tüketim hesapla
                              const periodData = mockData.yearlyTrend.find(p => p.period === d.period);
                              if (periodData) {
                                base[`ilce_${belediyeId}`] = Math.round(periodData.consumption * belediyeRatio);
                              }
                            }
                          });
                        }
                        return base;
                      })}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="period" 
                        angle={-45} 
                        textAnchor="end" 
                        height={100}
                        interval={3}
                      />
                      <YAxis 
                        yAxisId="left"
                        label={activeCard === 'population' ? { value: 'Su Tüketimi (M m³)', angle: -90, position: 'insideLeft' } : undefined}
                      />
                      {activeCard === 'population' && (
                        <YAxis 
                          yAxisId="right" 
                          orientation="right"
                          label={{ value: 'Nüfus (Milyon)', angle: 90, position: 'insideRight' }}
                        />
                      )}
                      <Tooltip 
                        formatter={(value, name) => {
                          if (name === 'population' || name === 'populationNormalized') {
                            return `${(value / 1000000).toFixed(1)}M kişi`;
                          }
                          if (name === 'consumption' || name === 'consumptionNormalized') {
                            return `${(value / 1000000).toFixed(2)}M m³`;
                          }
                          if (name === 'consumptionPerPerson') {
                            return `${value.toFixed(2)} L/kişi`;
                          }
                          return typeof value === 'number' ? value.toLocaleString('tr-TR') : value;
                        }}
                        labelFormatter={(label) => `Dönem: ${label}`}
                      />
                      <Legend />
                      {activeCard === 'population' ? (
                        <>
                          <Line 
                            type="monotone" 
                            dataKey="consumptionNormalized" 
                            stroke="#3b82f6" 
                            strokeWidth={2} 
                            name="Su Tüketimi (M m³)" 
                            yAxisId="left"
                            dot={{ r: 4 }} 
                          />
                          <Line 
                            type="monotone" 
                            dataKey="populationNormalized" 
                            stroke="#8b5cf6" 
                            strokeWidth={2} 
                            name="Nüfus (Milyon)" 
                            yAxisId="right"
                            dot={{ r: 4 }} 
                          />
                          <Line 
                            type="monotone" 
                            dataKey="consumptionPerPerson" 
                            stroke="#10b981" 
                            strokeWidth={2} 
                            strokeDasharray="5 5"
                            name="Kişi Başı Tüketim (L/kişi)" 
                            yAxisId="left"
                            dot={{ r: 4 }} 
                          />
                        </>
                      ) : activeCard === 'totalWaterConsumption' ? (
                        <>
                          <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} name="Toplam Su Tüketimi (m³)" dot={{ r: 4 }} />
                          <Line type="monotone" dataKey="teorik" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" name="Teorik Tüketim" dot={{ r: 4 }} />
                          {/* Seçilen ilçelerin tüketimlerini göster */}
                          {selectedIlcelerForChart.map((belediyeId, idx) => {
                            const belediye = mockData.belediyeler.find(b => b.id === belediyeId);
                            if (!belediye) return null;
                            const totalConsumption = mockData.belediyeler.reduce((sum, b) => sum + b.waterConsumption, 0);
                            const belediyeRatio = belediye.waterConsumption / totalConsumption;
                            const colors = ['#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];
                            return (
                              <Line 
                                key={belediyeId}
                                type="monotone" 
                                dataKey={`ilce_${belediyeId}`} 
                                stroke={colors[idx % colors.length]} 
                                strokeWidth={2} 
                                name={`${belediye.name} Tüketimi`} 
                                dot={{ r: 3 }} 
                              />
                            );
                          })}
                          {chartData.data[0]?.population && (
                            <>
                              <Line type="monotone" dataKey="population" stroke="#8b5cf6" strokeWidth={2} name="Nüfus" yAxisId="right" dot={{ r: 4 }} />
                              <YAxis yAxisId="right" orientation="right" />
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} name="Gerçek" dot={{ r: 4 }} />
                          <Line type="monotone" dataKey="teorik" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" name="Teorik" dot={{ r: 4 }} />
                        </>
                      )}
                    </LineChart>
                  ) : chartData.type === 'area' ? (
                    <AreaChart data={chartData.data.map(d => ({
                      ...d,
                      teorik: typeof d.value === 'number' ? d.value * 0.9 : d.value,
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" angle={-45} textAnchor="end" height={100} interval={3} />
                      <YAxis />
                      <Tooltip 
                        formatter={(value) => typeof value === 'number' ? value.toLocaleString('tr-TR') : value}
                        labelFormatter={(label) => `Dönem: ${label}`}
                      />
                      <Legend />
                      <Area type="monotone" dataKey="value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} name="Gerçek" />
                      <Area type="monotone" dataKey="teorik" stroke="#10b981" fill="#10b981" fillOpacity={0.2} name="Teorik" />
                    </AreaChart>
                  ) : (
                    <ComposedChart data={chartData.data.map(d => ({
                      ...d,
                      teorik: typeof d.value === 'number' ? d.value * 0.9 : d.value,
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" angle={-45} textAnchor="end" height={100} interval={3} />
                      <YAxis />
                      <Tooltip 
                        formatter={(value) => typeof value === 'number' ? value.toLocaleString('tr-TR') : value}
                        labelFormatter={(label) => `Dönem: ${label}`}
                      />
                      <Legend />
                      <Bar dataKey="value" fill="#3b82f6" name="Gerçek" />
                      <Bar dataKey="teorik" fill="#10b981" fillOpacity={0.7} name="Teorik" />
                    </ComposedChart>
                  )}
                </ResponsiveContainer>
              </div>
              
              {/* Genel Filtreleme Paneli - Grafiğin Altında */}
              {filterPanelsOpen.mainChart && (
                <div className={styles.chartFilterSection}>
                  <div className={styles.filterSectionHeader}>
                    <h3 className={styles.filterSectionTitle}>Grafiğe Eklenecek İlçeler</h3>
                    <button
                      className={styles.closeFilterButton}
                      onClick={() => toggleFilterPanel('mainChart')}
                      title="Filtreleri Kapat"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  
                  {/* İlçe Seçimi - Direkt Tıklama ile Ekleme */}
                  <div className={styles.ilceSelectionGrid}>
                    {mockData.belediyeler.map(belediye => (
                      <div
                        key={belediye.id}
                        className={`${styles.ilceSelectionCard} ${chartFilters.mainChart.belediyeler.includes(belediye.id) ? styles.ilceSelected : ''}`}
                        onClick={() => toggleChartBelediye('mainChart', belediye.id)}
                      >
                        <div className={styles.ilceCardCheckbox}>
                          <input
                            type="checkbox"
                            checked={chartFilters.mainChart.belediyeler.includes(belediye.id)}
                            onChange={() => toggleChartBelediye('mainChart', belediye.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className={styles.ilceCardInfo}>
                          <div className={styles.ilceCardName}>{belediye.name}</div>
                          <div className={styles.ilceCardStats}>
                            {belediye.parks} Park • {(belediye.waterConsumption / 1000).toFixed(0)}K m³
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {chartFilters.mainChart.belediyeler.length > 0 && (
                    <div className={styles.filterInfo}>
                      {chartFilters.mainChart.belediyeler.length} ilçe seçildi - Grafiğe eklendi
                    </div>
                  )}
                  
                  {/* Park Filtreleme - Seçilen ilçelerin parkları */}
                  {chartFilters.mainChart.belediyeler.length > 0 && (
                    <div className={styles.parkFilterSection}>
                      <h4 className={styles.filterGroupTitle} style={{ marginTop: '20px', marginBottom: '12px' }}>Park Filtreleme (Opsiyonel)</h4>
                      <div className={styles.parkFilterContainer}>
                        {chartFilters.mainChart.belediyeler.map(belediyeId => {
                          const belediye = mockData.belediyeler.find(b => b.id === belediyeId);
                          const parks = mockData.parks[belediyeId] || [];
                          return (
                            <div key={belediyeId} className={styles.parkFilterGroup}>
                              <h5 className={styles.parkFilterGroupTitle}>{belediye?.name}</h5>
                              <div className={styles.parkCheckboxList}>
                                {parks.slice(0, 15).map(park => (
                                  <label key={park.id} className={styles.checkboxLabel}>
                                    <input
                                      type="checkbox"
                                      checked={chartFilters.mainChart.parklar.includes(park.id)}
                                      onChange={() => toggleChartPark('mainChart', park.id)}
                                    />
                                    <span>{park.name}</span>
                                  </label>
                                ))}
                                {parks.length > 15 && (
                                  <div className={styles.moreParksInfo}>
                                    +{parks.length - 15} park daha
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {chartFilters.mainChart.parklar.length > 0 && (
                        <div className={styles.filterInfo} style={{ marginTop: '12px' }}>
                          {chartFilters.mainChart.parklar.length} park seçildi
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* İL / İLÇE TÜKETİM ÖZETİ - Ana Sayfanın Altında */}
          <div className={styles.ilIlceSection}>
            <div className={styles.sectionHeaderWithFilter}>
              <h2 className={styles.sectionTitle}>İL / İLÇE TÜKETİM ÖZETİ</h2>
              <button
                className={styles.filterToggleButton}
                onClick={() => toggleFilterPanel('ilIlceTable')}
                title={filterPanelsOpen.ilIlceTable ? 'Filtreleri Gizle' : 'Filtreleri Göster'}
              >
                <Filter size={18} />
                {filterPanelsOpen.ilIlceTable ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>
            
            {(() => {
              const filteredBelediyeler = selectedBelediyeler.length > 0
                ? mockData.belediyeler.filter(b => selectedBelediyeler.includes(b.id))
                : mockData.belediyeler;

              return (
                <>
                  {/* Tablo Filtreleme - Tablonun Altında */}
                  {filterPanelsOpen.ilIlceTable && (
                    <div className={styles.tableFilterSection}>
                      <div className={styles.filterSectionHeader}>
                        <h3 className={styles.filterSectionTitle}>Belediye Filtreleme</h3>
                        <button
                          className={styles.closeFilterButton}
                          onClick={() => toggleFilterPanel('ilIlceTable')}
                          title="Filtreleri Kapat"
                        >
                          <X size={18} />
                        </button>
                      </div>
                      <p className={styles.filterDescription}>
                        Hangi belediyelerin özet bilgisini görmek istediğinizi seçin
                      </p>
                      <div className={styles.checkboxGroup}>
                        {mockData.belediyeler.map(belediye => (
                          <label key={belediye.id} className={styles.checkboxLabel}>
                            <input
                              type="checkbox"
                              checked={tableFilters.ilIlceTable.belediyeler.includes(belediye.id)}
                              onChange={() => toggleTableBelediye('ilIlceTable', belediye.id)}
                            />
                            <span>{belediye.name}</span>
                          </label>
                        ))}
                      </div>
                      {tableFilters.ilIlceTable.belediyeler.length > 0 && (
                        <div className={styles.filterInfo}>
                          {tableFilters.ilIlceTable.belediyeler.length} belediye seçildi
                        </div>
                      )}
                    </div>
                  )}

                  <div className={styles.tableContainer}>
                    <table className={styles.dataTable}>
                      <thead>
                        <tr>
                          <th>Belediyeler</th>
                          <th>Nüfus</th>
                          <th>Toplam Park Sayısı</th>
                          <th>Toplam Park Alanı (m²)</th>
                          <th>Yeşil Alan (m²)</th>
                          <th>Toplam Su Tüketimi (m³)</th>
                          <th>Yeşil Alan Su Tüketimi (m³/park)</th>
                          <th>Yeşil Alan Su Tüketimi (m³/m²)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBelediyeler.map(belediye => {
                          const waterPerPark = belediye.waterConsumption / belediye.parks;
                          const waterPerM2 = belediye.waterConsumption / belediye.greenArea;
                          return (
                            <tr key={belediye.id}>
                              <td>{belediye.name}</td>
                              <td>{belediye.nufus.toLocaleString('tr-TR')}</td>
                              <td>{belediye.parks}</td>
                              <td>{belediye.parkArea.toLocaleString('tr-TR')}</td>
                              <td>{belediye.greenArea.toLocaleString('tr-TR')}</td>
                              <td>{(belediye.waterConsumption / 1000).toFixed(1)}K</td>
                              <td>{(waterPerPark / 1000).toFixed(2)}K</td>
                              <td>{waterPerM2.toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </div>
        </>
      )}

      {/* Sayfa Navigasyonu */}
      <div className={styles.pageNavigation}>
        <button
          className={`${styles.navButton} ${activePage === 'main' ? styles.active : ''}`}
          onClick={() => setActivePage('main')}
        >
          <BarChart3 size={18} />
          <span>Ana Sayfa</span>
        </button>
        <button
          className={`${styles.navButton} ${activePage === 'belediye' ? styles.active : ''}`}
          onClick={() => setActivePage('belediye')}
        >
          <Trees size={18} />
          <span>BELEDİYE ÖZETİ</span>
        </button>
        <button
          className={`${styles.navButton} ${activePage === 'reports' ? styles.active : ''}`}
          onClick={() => setActivePage('reports')}
        >
          <FileText size={18} />
          <span>RAPORLAR</span>
        </button>
      </div>

      {/* Sayfa İçerikleri */}
      {activePage === 'belediye' && renderBelediyeOzet()}
      {activePage === 'reports' && renderReports()}

      {/* Footer - Çıkış Butonu */}
      <div className={styles.footer}>
        <button className={styles.logoutButton} onClick={logout}>
          <LogOut size={18} />
          <span>Çıkış Yap</span>
        </button>
      </div>
    </div>
  );
};

export default LegacyAskiDashboard;
